'use client';

import { useState, useEffect } from 'react';
import { cn } from 'utils/styles';
import { Button, ListItem } from '@1debit/chimekit';
import {
  useTableOfContentsAnchorClickedTracking,
  useTableOfContentsToggleClickedTracking,
} from 'components/analytics/hooks';
import { trackTocToggleClicked, trackTocAnchorClicked } from 'components/analytics/eventHandlerTracking';
import { addIdsToHeadings, getHeadingDetails } from './util';

export interface TableOfContentsProps {
  title?: string;
  contentWrapper?: string;
  expandedByDefault?: boolean;
}

/**
 * @param title - the title of the table of contents
 * @param contentWrapper - the id of a div that contains h2s
 */
export const TableOfContents = ({
  title = 'Table of Contents',
  contentWrapper = 'blog-content-wrapper',
  expandedByDefault = false,
}: TableOfContentsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(expandedByDefault);
  const tocToggleTracking = useTableOfContentsToggleClickedTracking();
  const tocAnchorTracking = useTableOfContentsAnchorClickedTracking();
  const [items, setItems] = useState<{ label: string; anchor: string }[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    addIdsToHeadings(contentWrapper);
    const headingDetails = getHeadingDetails(contentWrapper);
    const filteredHeadingDetails = headingDetails.filter(
      (item): item is { label: string; anchor: string } => item.label !== null,
    );
    setItems(filteredHeadingDetails);
    if (filteredHeadingDetails.length === 0) {
      setIsDisabled(true);
      setIsOpen(false);
    }
  }, [contentWrapper]);

  const handleButtonClick = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    tocToggleTracking();
    trackTocToggleClicked({
      action: newIsOpen ? 'open' : 'close',
    });
  };

  const handleAnchorClick = (e: React.MouseEvent<HTMLElement>, anchor: string) => {
    trackTocAnchorClicked({
      anchor_label: `${e.currentTarget.textContent}: #${anchor}`,
    });
    tocAnchorTracking();
    const element = document.getElementById(anchor);
    if (element) {
      const yOffset = -80; // 80px offset from the top of page to account for the fixed header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      // Update URL without triggering scroll
      window.history.pushState({}, '', `#${anchor}`);
    }
  };

  return (
    <section id="table-of-contents">
      <div className="px-2 py-2 mx-auto rounded-md content-wrapper bg-bg-raised-secondary">
        <Button
          data-testid="tableOfContentsTitle"
          className="flex items-center justify-between w-full px-4 py-2 font-sans font-semibold rounded-sm text-md active:scale-100"
          analyticsId={null}
          variant="tertiary"
          iconProps={{ icon: isOpen ? 'remove_circle' : 'add_circle', position: 'trailing' }}
          onClick={handleButtonClick}
          disabled={isDisabled}>
          {title}
        </Button>
        {isOpen && (
          <div className="px-4">
            {items.map((item, index) => {
              return (
                <ListItem
                  key={item.label}
                  tag="button"
                  analyticsId={item.label}
                  onClick={e => handleAnchorClick(e, item.anchor)}
                  className={cn(
                    'px-0 py-0 hover:underline rounded-none hover:rounded-none active:scale-100 hover:cursor-pointer',
                    index !== items.length - 1 && 'border-b border-border-primary',
                  )}>
                  <ListItem.ListContent title={item.label} className="font-sans font-semibold text-md" />
                </ListItem>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default TableOfContents;
